<template>
  <div>
    <h1>
      {{ $t("invoice.details.invoiceNumber") }}: {{ invoice.number }}
      <v-chip v-if="invoice.left_to_pay === 0" label outlined style="color: #44d3af; background-color: rgba(30,255,200,0.22) !important;"
        >{{ $t("invoice.paid") }}
      </v-chip>
    </h1>
    <div class="mt-3 elevation-12  lighten-4">
      <v-row class="pa-4 white" v-if="loading">
        <v-col>
          <h2>{{ $t("global.loading") }}</h2>
        </v-col>
      </v-row>
      <v-row class="pa-4 white" v-if="!loading">
        <v-col>
          <v-row align-content="space-between">
            <v-col cols="6" sm="3">
              <v-text-field :value="invoice.net_in_currency + ' ' + invoice.currency_symbol" :label="$t('invoice.details.netAmount')" readonly>
                <div slot="prepend">
                  <v-icon style="font-weight: normal">fa-money-bill-alt</v-icon>
                </div>
              </v-text-field>
            </v-col>
            <v-col cols="6" sm="3">
              <v-text-field :value="invoice.gross_in_currency + ' ' + invoice.currency_symbol" :label="$t('invoice.details.grossAmount')" readonly>
                <div slot="prepend" class="ml-2">
                  <v-icon>fa-money-bill-alt</v-icon>
                </div>
              </v-text-field>
            </v-col>
            <v-col cols="6" sm="3">
              <v-text-field v-model="invoice.issue_date" :label="$t('invoice.dataTableHeaders.issueDate')" readonly>
                <div slot="prepend" class="ml-2">
                  <v-icon>fa-file-invoice-dollar</v-icon>
                </div>
              </v-text-field>
            </v-col>
            <v-col cols="6" sm="3">
              <v-text-field v-model="invoice.sale_date" :label="$t('invoice.dataTableHeaders.sellDate')" readonly>
                <div slot="prepend" class="ml-2">
                  <v-icon style="font-weight: normal">fa-calendar-alt</v-icon>
                </div>
              </v-text-field>
            </v-col>
            <v-col cols="6" sm="3">
              <v-text-field v-if="invoice.left_to_pay !== 0" v-model="invoice.left_to_pay" :label="$t('invoice.dataTableHeaders.leftToPay')" readonly>
                <div slot="prepend" class="ml-2">
                  <v-icon style="font-weight: normal; color: orange;">warning</v-icon>
                </div>
              </v-text-field>
            </v-col>
            <v-spacer />
            <v-col cols="6" sm="3">
              <v-btn color="accent" :disabled="$store.getters['Ajax/loading']" block @click="downloadInvoice(invoice)">{{ $t("invoice.download") }}</v-btn>
            </v-col>
          </v-row>

          <h2>{{ $t("invoice.details.items") }}</h2>

          <div v-for="invoiceItem in invoice.invoiceItems" :key="invoiceItem.serial_index">
            <fieldset class="pd-1 custom-fieldset">
              <legend class="primary--text">{{ $t("invoice.entry") }} {{ invoiceItem.serial_index }}</legend>
              <v-row>
                <v-col cols="1" md="1" class="text-center align-content-center fill-height align-content-center">
                  <v-icon style="font-size: 46px; color: rgba(0, 0, 0, 0.32);">info</v-icon>
                </v-col>
                <v-col cols="11" md="3">
                  <v-row>
                    <v-text-field
                      :label="$t('invoice.dataTableHeaders.gross')"
                      disabled
                      class="no-line-after-input"
                      :value="invoiceItem.gross_in_currency + ' ' + invoice.currency_symbol"
                    >
                    </v-text-field>
                    <v-text-field
                      :label="$t('invoice.dataTableHeaders.net')"
                      class="no-line-after-input"
                      disabled
                      :value="invoiceItem.net_price_in_currency + ' ' + invoice.currency_symbol"
                    >
                    </v-text-field>
                  </v-row>
                </v-col>
                <v-col cols="11" md="7">
                  <v-text-field :label="$t('global.itemName')" disabled class="no-line-after-input" :value="invoiceItem.name"> </v-text-field>
                </v-col>
                <v-col cols="6" md="1" v-if="invoiceItem.xtur_booking_id != null">
                  <v-tooltip bottom>
                    {{ $t("booking.bookingDetails") }}
                    <template #activator="{on}">
                      <v-btn disabled icon v-on="on">
                        <v-icon>fa-suitcase</v-icon>
                      </v-btn>
                    </template>
                  </v-tooltip>
                </v-col>
              </v-row>
            </fieldset>
          </div>

          <h2>{{ $t("invoice.details.payments") }}</h2>

          <div v-if="invoice.payments.length === 0">
            {{ $t("invoice.details.noPaymentsYet") }}
          </div>

          <div v-for="payment in invoice.payments" :key="payment.id">
            <fieldset class="pd-1 custom-fieldset">
              <legend class="primary--text">{{ $t("$staticData.paymentTranslations." + payment.payment_type.replace(/[."\s]/g, "")) }}</legend>
              <v-row>
                <v-col cols="1" md="1" class="text-center align-content-center fill-height align-content-center">
                  <v-icon
                    v-if="payment.payment_type.match(/przelew/i) || payment.payment_type.match(/kompensata/i)"
                    style="font-size: 46px; color: rgba(0, 0, 0, 0.32);"
                    >fa-university
                  </v-icon>
                  <v-icon v-if="payment.payment_type.match(/gotówka/i)" style="font-size: 46px; color: rgba(0, 0, 0, 0.32);">fa-money-bill-alt </v-icon>
                </v-col>
                <v-col cols="5" md="2">
                  <v-text-field
                    :label="$t('global.amount')"
                    disabled
                    class="no-line-after-input"
                    :value="payment.amount_in_payment_currency + ' ' + payment.payment_currency"
                  >
                  </v-text-field>
                </v-col>
                <v-col cols="6" md="3">
                  <v-text-field :label="$t('invoice.details.paymentDate')" class="no-line-after-input" disabled :value="payment.date"> </v-text-field>
                </v-col>
                <v-col cols="6" md="3" v-if="payment.payment_currency !== invoice.currency_symbol">
                  <v-text-field
                    :label="$t('invoice.details.amountInDocumentCurrency')"
                    disabled
                    class="no-line-after-input"
                    :value="payment.amount_in_document_currency + ' ' + invoice.currency_symbol"
                  >
                  </v-text-field>
                </v-col>
                <v-col cols="6" md="3" v-if="payment.payment_currency !== invoice.currency_symbol">
                  <v-text-field
                    :label="$t('exchangeRate')"
                    disabled
                    class="no-line-after-input"
                    :value="payment.exchange_rate + ' (' + payment.exchange_rate_date + ')'"
                  >
                  </v-text-field>
                </v-col>
              </v-row>
            </fieldset>
          </div>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import InvoiceModel from "../../models/invoice";

export default {
  name: "InvoicesDetailsView",
  data: () => ({
    invoice: new InvoiceModel()
  }),
  computed: {
    invoices: function() {
      return this.$store.getters["Invoice/invoiceList"];
    },
    loading() {
      return this.$store.getters["Ajax/loading"];
    }
  },
  methods: {
    downloadInvoice: function(invoice) {
      this.$store.dispatch("Invoice/downloadInvoiceFile", invoice);
    }
  },
  created() {
    if (typeof this.$route.params.id !== "undefined") {
      this.invoice.load(this.$route.params.id);
    }
  },
  mounted() {
    this.$store.dispatch("Visual/setHalfBackground");
  },
  beforeDestroy() {
    this.$store.dispatch("Visual/setWhiteBackground");
  }
};
</script>

<style>
fieldset {
  margin-bottom: 24px;
  padding: 16px 24px 0;
  border: 1px solid rgba(0, 0, 0, 0.12);
}

legend {
  border: 0;
  color: inherit;
  display: table;
  max-width: 100%;
  white-space: normal;
  margin-left: 24px;
  padding: 0 12px;
  font-weight: 700;
  text-transform: uppercase;
}

.theme--light .v-text-field.v-input--is-disabled .v-input__slot:before,
.application .theme--light.v-text-field.v-input--is-disabled .v-input__slot:before {
}

.no-line-after-input.v-input--is-disabled .v-input__slot:before,
.application .no-line-after-input.v-input--is-disabled .v-input__slot:before {
  border-image: none;
  border-style: none;
}

.no-line-after-input.v-input--is-disabled input,
.no-line-after-input.v-input--is-disabled input {
  color: black;
}
</style>
